import React, { useEffect, useState } from "react";
import {
  Grid,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Input,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { makeStyles } from "@mui/styles";

import { useParams } from "react-router-dom";
import axios from "axios";
import config from "../config.js";
import swal from "sweetalert";
import Cookies from "universal-cookie";
import mixpanel from "mixpanel-browser";

// Define custom styles using makeStyles to match your theme
const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(3),
    borderRadius: "8px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
    backgroundColor: "#424242",
    marginTop: theme.spacing(4),
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  textField: {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#6a6a6a",
      },
      "&:hover fieldset": {
        borderColor: "#9c9c9c",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#4a6da7",
      },
    },
    "& .MuiInputLabel-outlined": {
      color: "#e0e0e0",
    },
    "& .MuiOutlinedInput-input": {
      color: "#ffffff",
    },
    marginBottom: theme.spacing(1),
  },
  formControl: {
    marginBottom: theme.spacing(1),
    "& .MuiInputLabel-root": {
      color: "#e0e0e0",
    },
    "& .MuiInput-root": {
      color: "#ffffff",
      "&:before": {
        borderBottomColor: "#6a6a6a",
      },
      "&:hover:not(.Mui-disabled):before": {
        borderBottomColor: "#9c9c9c",
      },
      "&:after": {
        borderBottomColor: "#4a6da7",
      },
    },
    "& .MuiIconButton-root": {
      color: "#e0e0e0",
    },
  },
  button: {
    backgroundColor: "#3f51b5",
    color: "#ffffff",
    padding: "12px",
    marginTop: theme.spacing(2),
    "&:hover": {
      backgroundColor: "#303f9f",
    },
  },
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  loadingText: {
    textAlign: "center",
    color: "#e0e0e0",
    marginTop: theme.spacing(8),
  },
  disabledInput: {
    "& .MuiOutlinedInput-input": {
      color: "#a0a0a0",
    },
  },
}));

const RegisterUser = () => {
  const classes = useStyles();
  const [isFormShow, setIsFormShow] = useState(false);
  const [username, setUsername] = useState("");
  const [fullname, setFullname] = useState("");
  const [contact, setContact] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { token } = useParams();
  const cookies = new Cookies();

  useEffect(() => {
    axios
      .post(config.BACKEND_URL + "/invite-user/is-token-valid", { token })
      .then((res) => {
        if (res.status !== 200) {
          window.location.replace("http://localhost:3003/");
        } else {
          setEmail(res.data.email);
          setIsFormShow(true);
        }
      })
      .catch((err) => {
        window.location.replace("http://localhost:3003/");
      });
  }, [token]);

  const handleSubmit = (event) => {
    event.preventDefault();
    mixpanel.track("User Register Invite Submit Button Clicked", {});
    if (password === confirmPassword) {
      swal({
        title: "Are you sure?",
        text: "Do you want to register?",
        icon: "warning",
        buttons: true,
        dangerMode: true,
      }).then(async (willDelete) => {
        if (willDelete) {
          axios
            .post(config.BACKEND_URL + "/invite-user/create", {
              username,
              fullname,
              email,
              contact,
              password,
              token,
            })
            .then((res) => {
              if (res.status === 200) {
                swal(
                  "User created successfully!",
                  "Check mail to authenticate",
                  "success"
                ).then(() => window.location.replace("http://localhost:3003"));
              } else {
                swal("Error", `Process Failed ${res.data.message}`, "error");
              }
            })
            .catch((err) => {
              console.log(err);
              swal(
                "Error",
                err.response?.data?.message || "An error occurred",
                "error"
              );
            });
        } else {
          swal("User not added!", "", "info");
        }
      });
    } else {
      swal("Password mismatch!", "Process failed. Try again.", "error");
    }
  };

  const handleUsernameChange = (event) => {
    setUsername(event.target.value);
  };

  const handleFullnameChange = (event) => {
    setFullname(event.target.value);
  };

  const handleContactChange = (event) => {
    setContact(event.target.value);
  };

  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
  };

  const handleConfirmPasswordChange = (event) => {
    setConfirmPassword(event.target.value);
  };

  const handleShowPasswordClick = () => {
    setShowPassword(!showPassword);
  };

  const handleShowConfirmPasswordClick = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <div className={classes.container}>
      <link
        href="https://fonts.googleapis.com/icon?family=Material+Icons"
        rel="stylesheet"
      />
      {isFormShow ? (
        <Grid container justifyContent="center">
          <Grid item xs={12} sm={8} md={6} lg={4}>
            <Paper className={classes.paper}>
              <form onSubmit={handleSubmit} className={classes.form}>
                <TextField
                  label="Username"
                  variant="outlined"
                  fullWidth
                  required
                  value={username}
                  onChange={handleUsernameChange}
                  className={classes.textField}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                <TextField
                  label="Full Name"
                  variant="outlined"
                  fullWidth
                  required
                  value={fullname}
                  onChange={handleFullnameChange}
                  className={classes.textField}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                <TextField
                  label="Contact"
                  variant="outlined"
                  fullWidth
                  required
                  value={contact}
                  onChange={handleContactChange}
                  className={classes.textField}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                <TextField
                  label="Email"
                  variant="outlined"
                  fullWidth
                  required
                  type="email"
                  value={email}
                  disabled
                  className={`${classes.textField} ${classes.disabledInput}`}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                <FormControl className={classes.formControl} fullWidth required>
                  <InputLabel htmlFor="password">Password</InputLabel>
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={handlePasswordChange}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleShowPasswordClick}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                </FormControl>
                <FormControl className={classes.formControl} fullWidth required>
                  <InputLabel htmlFor="confirm-password">
                    Confirm Password
                  </InputLabel>
                  <Input
                    id="confirm-password"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={handleConfirmPasswordChange}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleShowConfirmPasswordClick}
                          edge="end"
                        >
                          {showConfirmPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                </FormControl>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  className={classes.button}
                >
                  REGISTER
                </Button>
              </form>
            </Paper>
          </Grid>
        </Grid>
      ) : (
        <h1 className={classes.loadingText}>Loading...</h1>
      )}
    </div>
  );
};

export default RegisterUser;
